import { useEffect, useRef, useState } from 'react';
import { getClaims, useAuth } from '@workos-inc/authkit-react';
import { createAccessRequest, listMyAccessRequests, requestErrorText, type MyAccessRequest } from '../api/accessRequests';
import { holdsRoleFor } from '../lib/accessState';
import { logEvent } from '../lib/telemetry';

const REQUESTED = 'cloudhouse.enterprise.access_request.requested';
const REQUEST_FAILED = 'cloudhouse.enterprise.access_request.request_failed';

type Auth = { getAccessToken: (options?: { forceRefresh?: boolean }) => Promise<string | null | undefined> };

function rolesFromToken(token: string): string[] {
  const claims = getClaims(token) as { roles?: string[]; role?: string };
  return claims.roles ?? (claims.role ? [claims.role] : []);
}

// One hook serves every card on the landing page: the member's own requests, one create at a
// time per product, and one forced token refresh per request that turns out to be granted.
export function useAccessRequests({ enabled, roles, onGranted }: {
  enabled: boolean;
  roles: readonly string[] | null | undefined;
  onGranted?: () => void;
}) {
  const auth = useAuth() as unknown as Auth;
  const [requests, setRequests] = useState<MyAccessRequest[]>([]);
  const [settled, setSettled] = useState(false);
  const [freshRoles, setFreshRoles] = useState<readonly string[] | null>(null);
  const [busyProducts, setBusyProducts] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Grants already refreshed for; written and read only inside the effect below.
  const refreshed = useRef(new Set<string>());

  async function load(signal?: AbortSignal) {
    const token = await auth.getAccessToken();
    if (!token) return;
    const { data } = await listMyAccessRequests(token, signal);
    if (data) setRequests(data.requests);
  }

  useEffect(() => {
    if (!enabled) return;
    const ctrl = new AbortController();
    void (async () => {
      try { await load(ctrl.signal); } catch { /* aborted or unreachable: the cards fall back to Request access */ }
      finally { if (!ctrl.signal.aborted) setSettled(true); }
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once when enabled flips on
  }, [enabled]);

  const known = freshRoles ?? roles;
  const staleIds = requests
    .filter((r) => r.state === 'granted' && !holdsRoleFor(known, r.productId))
    .map((r) => r.id)
    .join(',');

  useEffect(() => {
    const next = staleIds.split(',').filter((id) => id && !refreshed.current.has(id));
    if (next.length === 0) return;
    for (const id of next) refreshed.current.add(id);
    void (async () => {
      try {
        const token = await auth.getAccessToken({ forceRefresh: true });
        if (token) setFreshRoles(rolesFromToken(token));
      } catch { /* the next scheduled refresh carries the new role */ }
      onGranted?.();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the stale request ids; auth and onGranted are read at fire time
  }, [staleIds]);

  const clear = () => { setError(null); setSuccess(null); };

  async function request(productKey: string, productName: string) {
    if (busyProducts.has(productKey)) return;
    clear();
    setBusyProducts((prev) => new Set(prev).add(productKey));
    try {
      const token = await auth.getAccessToken();
      if (!token) { setError("We couldn't verify your session. Please refresh and try again."); return; }
      const { data, error: apiError, response } = await createAccessRequest(token, productKey);
      if (apiError || !data) {
        const code = (apiError as { error?: string } | undefined)?.error;
        logEvent(REQUEST_FAILED, 'Access request refused', { product_id: productKey, status: response?.status ?? 0, code }, 'WARN');
        setError(requestErrorText(response?.status, code));
        return;
      }
      logEvent(REQUESTED, 'Member requested access', { product_id: productKey, request_id: data.id });
      setRequests((prev) => [...prev.filter((r) => r.id !== data.id), data]);
      setSuccess(`Your request for ${productName} has been sent to your organisation admins.`);
    } catch {
      logEvent(REQUEST_FAILED, 'Access request refused', { product_id: productKey, status: 0, code: undefined }, 'WARN');
      setError(requestErrorText(undefined, undefined));
    } finally {
      setBusyProducts((prev) => { const next = new Set(prev); next.delete(productKey); return next; });
    }
  }

  const openProducts = new Set(requests.filter((r) => r.state === 'open').map((r) => r.productId));
  const requestedAt = new Map(requests.filter((r) => r.state === 'open').map((r) => [r.productId, r.requestedAt]));
  const loading = enabled && !settled;

  return { openProducts, requestedAt, freshRoles, loading, request, busyProducts, error, success, clear };
}
